import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Download, Upload, FileSpreadsheet, CheckCircle, 
  XCircle, AlertCircle, Loader2, HelpCircle, ChevronDown, ChevronUp,
  Info, X, ExternalLink
} from 'lucide-react';
import { inventoryAPI } from '../../api';
import toast from 'react-hot-toast';

const InventoryBulkUpload = () => {
  const navigate = useNavigate();
  const [instructions, setInstructions] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [file, setFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [skuLimit, setSkuLimit] = useState(null);

  useEffect(() => {
    fetchInstructions();
    fetchSkuLimit();
  }, []);

  const fetchInstructions = async () => {
    try {
      const response = await inventoryAPI.getUploadInstructions();
      setInstructions(response.data);
    } catch (error) {
      console.error('Failed to load instructions:', error);
    }
  };

  const fetchSkuLimit = async () => {
    try {
      const response = await inventoryAPI.checkSkuLimit();
      setSkuLimit(response.data);
    } catch (error) {
      console.error('SKU limit check failed:', error);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await inventoryAPI.downloadTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'nanokart_product_upload_template.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setValidationResult(null);
      setUploadResult(null);
    }
  };

  const handleValidate = async () => {
    if (!file) return;

    try {
      setValidating(true);
      setValidationResult(null);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await inventoryAPI.validateCSV(formData);
      setValidationResult(response.data);
      
      if (response.data.ready_to_upload) {
        toast.success(`Validation passed! ${response.data.valid} products ready to upload.`);
      } else if (response.data.invalid > 0) {
        toast.error(`Found ${response.data.invalid} errors. Please fix and try again.`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Validation failed';
      toast.error(detail);
    } finally {
      setValidating(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);
      setUploadResult(null);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await inventoryAPI.bulkUpload(formData);
      setUploadResult(response.data);
      
      if (response.data.success > 0) {
        toast.success(`Successfully uploaded ${response.data.success} products!`);
      }
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} products failed to upload.`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Upload failed';
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setValidationResult(null);
    setUploadResult(null);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6 px-1" data-testid="bulk-upload-page">
      {/* Header */}
      <div className="flex items-center gap-3 sm:gap-4">
        <Link
          to="/partners/inventory"
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Bulk Upload Products</h1>
          <p className="text-slate-500 text-sm">Import multiple products using a CSV file</p>
        </div>
      </div>

      {/* SKU Limit Warning */}
      {skuLimit && !skuLimit.can_add_more && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800 text-sm">SKU limit reached</p>
            <p className="text-sm text-red-600 mt-0.5">
              You have {skuLimit.current_count}/{skuLimit.max_allowed} SKUs. 
              <Link to="/partners/subscription" className="ml-1 underline hover:no-underline">
                Upgrade your plan
              </Link> to add more products.
            </p>
          </div>
        </div>
      )}

      {skuLimit && skuLimit.can_add_more && skuLimit.remaining !== 'unlimited' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            You can add up to <strong>{skuLimit.remaining}</strong> more products ({skuLimit.current_count}/{skuLimit.max_allowed} used).
          </p>
        </div>
      )}

      {/* Instructions Panel */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-slate-200 overflow-hidden"
      >
        <button
          onClick={() => setShowInstructions(!showInstructions)}
          className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition-colors"
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <HelpCircle className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-blue-900 text-sm sm:text-base">How to Upload Products in Bulk</span>
          </div>
          {showInstructions ? (
            <ChevronUp className="w-5 h-5 text-blue-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-blue-600" />
          )}
        </button>
        
        <AnimatePresence>
          {showInstructions && instructions && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 sm:p-6 space-y-4">
                {/* Steps */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2 text-sm sm:text-base">Steps:</h3>
                  <ol className="space-y-2">
                    {instructions.instructions.steps.map((step, idx) => (
                      <li key={idx} className="text-slate-600 flex items-start gap-2 text-xs sm:text-sm">
                        <span className="text-blue-600 font-medium flex-shrink-0">{idx + 1}.</span>
                        <span>{step.replace(/^\d+\.\s*/, '')}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Required vs Optional Columns */}
                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                  <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                    <h4 className="font-semibold text-green-800 mb-2 text-sm">Required Columns</h4>
                    <ul className="space-y-1.5 text-xs sm:text-sm">
                      {Object.entries(instructions.columns.required).map(([key, desc]) => (
                        <li key={key} className="text-green-700">
                          <span className="font-mono font-medium text-xs">{key}</span>
                          <span className="text-green-600 text-xs block ml-2 sm:inline sm:ml-1">- {desc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 sm:p-4">
                    <h4 className="font-semibold text-slate-800 mb-2 text-sm">Optional Columns</h4>
                    <ul className="space-y-1.5 text-xs sm:text-sm">
                      {Object.entries(instructions.columns.optional).map(([key, desc]) => (
                        <li key={key} className="text-slate-700">
                          <span className="font-mono font-medium text-xs">{key}</span>
                          <span className="text-slate-500 text-xs block ml-2 sm:inline sm:ml-1">- {desc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Valid Values */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                    <h4 className="font-semibold text-purple-800 mb-2 text-sm">Valid Moments</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {instructions.valid_moments.map(m => (
                        <span key={m} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded font-mono">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 sm:p-4">
                    <h4 className="font-semibold text-amber-800 mb-2 text-sm">Valid Categories</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {instructions.valid_categories.map(c => (
                        <span key={c} className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Tips */}
                <div className="bg-slate-100 rounded-lg p-3 sm:p-4">
                  <h4 className="font-semibold text-slate-800 mb-2 text-sm">💡 Tips</h4>
                  <ul className="space-y-1 text-xs sm:text-sm text-slate-600">
                    {instructions.tips.map((tip, idx) => (
                      <li key={idx}>• {tip}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Step 1: Download Template */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6"
      >
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm sm:text-base flex-shrink-0">
            1
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 mb-1 text-sm sm:text-base">Download CSV Template</h3>
            <p className="text-slate-500 text-xs sm:text-sm mb-3 sm:mb-4">
              Get the template with column headers and example data
            </p>
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <Download className="w-4 h-4" />
              Download Template
            </button>
          </div>
        </div>
      </motion.div>

      {/* Step 2: Upload CSV */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6"
      >
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm sm:text-base flex-shrink-0">
            2
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 mb-1 text-sm sm:text-base">Upload Your CSV File</h3>
            <p className="text-slate-500 text-xs sm:text-sm mb-3 sm:mb-4">
              Fill the template with your product data and upload
            </p>
            
            {!file ? (
              <label className="block border-2 border-dashed border-slate-200 rounded-lg p-6 sm:p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                <FileSpreadsheet className="w-10 h-10 sm:w-12 sm:h-12 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600 mb-1 text-sm">Click to select CSV file</p>
                <p className="text-xs text-slate-400">or drag and drop</p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            ) : (
              <div className="border border-slate-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <FileSpreadsheet className="w-7 h-7 sm:w-8 sm:h-8 text-green-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate">{file.name}</p>
                      <p className="text-xs text-slate-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleReset}
                    className="text-slate-400 hover:text-slate-600 text-xs sm:text-sm flex-shrink-0"
                  >
                    Change
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Step 3: Validate & Upload */}
      {file && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6"
        >
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm sm:text-base flex-shrink-0">
              3
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 mb-1 text-sm sm:text-base">Validate & Upload</h3>
              <p className="text-slate-500 text-xs sm:text-sm mb-3 sm:mb-4">
                First validate your file, then upload products
              </p>
              
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={handleValidate}
                  disabled={validating || uploading}
                  className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50 text-sm"
                >
                  {validating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  {validating ? 'Validating...' : 'Validate CSV'}
                </button>
                
                <button
                  onClick={handleUpload}
                  disabled={uploading || validating || (validationResult && !validationResult.ready_to_upload) || (skuLimit && !skuLimit.can_add_more)}
                  className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  {uploading ? 'Uploading...' : 'Upload Products'}
                </button>
              </div>
              
              {uploading && (
                <p className="mt-3 text-xs sm:text-sm text-blue-600">
                  ⏳ This may take a few minutes while downloading images...
                </p>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Validation Results */}
      {validationResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-xl border p-4 sm:p-6 ${
            validationResult.ready_to_upload 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}
        >
          <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 text-sm sm:text-base">
            {validationResult.ready_to_upload ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600" />
            )}
            Validation Results
          </h3>
          
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
            <div className="text-center p-2 sm:p-3 bg-white rounded-lg">
              <p className="text-xl sm:text-2xl font-bold text-green-600">{validationResult.valid}</p>
              <p className="text-xs sm:text-sm text-slate-600">Valid</p>
            </div>
            <div className="text-center p-2 sm:p-3 bg-white rounded-lg">
              <p className="text-xl sm:text-2xl font-bold text-red-600">{validationResult.invalid}</p>
              <p className="text-xs sm:text-sm text-slate-600">Invalid</p>
            </div>
            <div className="text-center p-2 sm:p-3 bg-white rounded-lg">
              <p className="text-xl sm:text-2xl font-bold text-amber-600">{validationResult.duplicates}</p>
              <p className="text-xs sm:text-sm text-slate-600">Duplicates</p>
            </div>
          </div>
          
          {validationResult.errors.length > 0 && (
            <div className="bg-white rounded-lg p-3 sm:p-4 max-h-48 sm:max-h-60 overflow-y-auto">
              <h4 className="font-medium text-red-800 mb-2 text-sm">Errors Found:</h4>
              <ul className="space-y-2 text-xs sm:text-sm">
                {validationResult.errors.slice(0, 20).map((err, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-red-500 font-mono flex-shrink-0">Row {err.row}:</span>
                    <span className="text-slate-600">
                      {err.sku_id && <span className="font-medium">[{err.sku_id}]</span>} {err.errors.join(', ')}
                    </span>
                  </li>
                ))}
                {validationResult.errors.length > 20 && (
                  <li className="text-slate-400 text-xs">
                    ... and {validationResult.errors.length - 20} more errors
                  </li>
                )}
              </ul>
            </div>
          )}
        </motion.div>
      )}

      {/* Upload Results */}
      {uploadResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6"
        >
          <h3 className="font-semibold text-slate-900 mb-3 text-sm sm:text-base">Upload Results</h3>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4">
            <div className="text-center p-2 sm:p-4 bg-slate-50 rounded-lg">
              <p className="text-lg sm:text-2xl font-bold text-slate-700">{uploadResult.total}</p>
              <p className="text-xs sm:text-sm text-slate-600">Total</p>
            </div>
            <div className="text-center p-2 sm:p-4 bg-green-50 rounded-lg">
              <p className="text-lg sm:text-2xl font-bold text-green-600">{uploadResult.success}</p>
              <p className="text-xs sm:text-sm text-slate-600">Success</p>
            </div>
            <div className="text-center p-2 sm:p-4 bg-red-50 rounded-lg">
              <p className="text-lg sm:text-2xl font-bold text-red-600">{uploadResult.failed}</p>
              <p className="text-xs sm:text-sm text-slate-600">Failed</p>
            </div>
            <div className="text-center p-2 sm:p-4 bg-amber-50 rounded-lg">
              <p className="text-lg sm:text-2xl font-bold text-amber-600">{uploadResult.skipped}</p>
              <p className="text-xs sm:text-sm text-slate-600">Skipped</p>
            </div>
          </div>
          
          {/* Detailed Results */}
          <div className="bg-slate-50 rounded-lg p-3 sm:p-4 max-h-60 sm:max-h-80 overflow-y-auto">
            <h4 className="font-medium text-slate-800 mb-2 text-sm">Details:</h4>
            <div className="space-y-2">
              {uploadResult.details.map((item, idx) => (
                <div 
                  key={idx} 
                  className={`flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 p-2 rounded text-xs sm:text-sm ${
                    item.status === 'success' ? 'bg-green-100' :
                    item.status === 'skipped' ? 'bg-amber-100' : 'bg-red-100'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {item.status === 'success' && <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />}
                    {item.status === 'skipped' && <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />}
                    {item.status === 'failed' && <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />}
                    <span className="font-mono truncate">{item.sku_id || `Row ${item.row}`}</span>
                  </div>
                  <div className="text-right pl-6 sm:pl-0">
                    {item.status === 'success' && (
                      <span className="text-green-700">{item.images_uploaded} images</span>
                    )}
                    {item.errors?.length > 0 && (
                      <span className="text-red-700">{item.errors[0]}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
            <button
              onClick={() => navigate('/partners/inventory')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              View Inventory
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 text-sm"
            >
              Upload More
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default InventoryBulkUpload;
